import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'

import {
  readDraft,
  readSignupCommunityLogoDataUrl,
} from './signupState'
import { subscribeSetupVenueCompletingTransition } from './setupVenueCompletingTransitionController'

/**
 * SetupVenueCompletingTransition — the 2-second "your venue is ready"
 * climax that plays after the venue wizard's step 2. Three-beat
 * sequence mirrors the community variant
 * (SetupCompletingTransition.jsx) but with venue-specific copy and
 * court chips instead of venue thumbnails.
 *
 *   t=0     "Setting up your venue." | ring + logo fade in | ring
 *           begins filling (linear, 1900ms total)
 *   t=700   "Adding your courts." | court chips cascade in
 *           (250ms each, 80ms stagger, starting at t=750)
 *   t=1400  "Your venue is ready."
 *   t=1750  Ring completion pulse (scale 1→1.04→1, brightness bump)
 *   t=1900  Ring reaches 100%
 *   t=2000  Whole loader fades out (400ms)
 *   t=2400  onComplete() — loader unmounts
 *
 * Navigation: the wizard's Continue handler calls navigate() in the
 * same tick as it triggers this loader, so /venue-owner/welcome
 * mounts BENEATH the loader from t=0. The loader's z-50 opaque
 * background keeps the welcome page hidden until the fade-out
 * reveals it.
 *
 * Previously the loader fired navigate() from a setTimeout inside
 * its own useEffect at t=1500ms ("mid-timeline so the route swap
 * stays hidden"). That coupled the loader's lifecycle to Router
 * state: the navigate triggered a Router re-render → the App-root
 * mount component re-rendered → a fresh onComplete prop was passed
 * → the loader's useEffect deps changed → cleanup cleared every
 * pending timer (including the one that fires onComplete to
 * dismiss the loader). Net effect: loader stuck visible forever.
 *
 * Synchronously navigating from the step (SetupVenueWizardCourts'
 * handleContinue) sidesteps the loop entirely — same pattern the
 * community wizard's policies step uses with the community loader.
 *
 * Mount point: SetupVenueCompletingTransitionMount (default export)
 * is rendered once in App.jsx as a sibling to <Routes>, paired with
 * the community mount. It subscribes to the venue controller pub/sub
 * and toggles its own visibility.
 *
 * Reduced motion: skip the whole animation, fire onComplete on the
 * next tick. The user lands on /venue-owner/welcome without a
 * visible loader.
 */

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1]

const RING_SIZE = 140
const RING_STROKE = 3
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

const T = {
  beat2: 700,
  beat3: 1400,
  pulse: 1750,
  fadeOutStart: 2000,
  unmount: 2400,
}

const RING_FILL_DURATION_S = 1.9
const RING_FADE_IN_DURATION_S = 0.3
const FADE_OUT_DURATION_S = 0.4

function readCourtsFromDraft() {
  try {
    const draft = readDraft()
    const c = draft.venue?.courts
    return Array.isArray(c) ? c : []
  } catch {
    return []
  }
}

/* ─────────────── Loader ─────────────── */

function SetupVenueCompletingTransition({ onComplete }) {
  const reduceMotion = useReducedMotion()

  // Snapshot data on mount — sessionStorage may be cleared or
  // mutated by the welcome page mounting underneath, and the loader
  // should never visibly change after it starts.
  const [logoDataUrl] = useState(readSignupCommunityLogoDataUrl)
  const [courts] = useState(readCourtsFromDraft)

  const [beatIdx, setBeatIdx] = useState(0)
  const [pulse, setPulse] = useState(false)
  const [fading, setFading] = useState(false)

  const beatTexts = [
    'Setting up your venue.',
    courts.length > 0 ? 'Adding your courts.' : 'Preparing your portal.',
    'Your venue is ready.',
  ]

  // Court chips: max 4 visible. With >4 courts, show 3 + "+N more".
  const showOverflow = courts.length > 4
  const visibleCourts = showOverflow ? courts.slice(0, 3) : courts
  const overflowCount = courts.length - 3

  useEffect(() => {
    if (reduceMotion) {
      // Reduced motion: skip the animation and unmount on next tick.
      // The step's handleContinue has already navigated, so the
      // welcome page is mounted underneath.
      const t = setTimeout(() => onComplete?.(), 0)
      return () => clearTimeout(t)
    }
    const timers = [
      setTimeout(() => setBeatIdx(1), T.beat2),
      setTimeout(() => setBeatIdx(2), T.beat3),
      setTimeout(() => setPulse(true), T.pulse),
      setTimeout(() => setFading(true), T.fadeOutStart),
      setTimeout(() => onComplete?.(), T.unmount),
    ]
    return () => timers.forEach(clearTimeout)
  }, [reduceMotion, onComplete])

  if (reduceMotion) return null

  const containerStagger = {
    hidden: {},
    show: {
      transition: {
        delayChildren: 0.75,
        staggerChildren: 0.08,
      },
    },
  }
  const itemFadeUp = {
    hidden: { opacity: 0, y: 8 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.25, ease: EASE_OUT_EXPO },
    },
  }

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center font-sans bg-[var(--signup-bg-base)] text-[var(--signup-text-primary)]"
      initial={{ opacity: 0 }}
      animate={{ opacity: fading ? 0 : 1 }}
      transition={{
        duration: fading ? FADE_OUT_DURATION_S : 0.2,
        ease: EASE_OUT_EXPO,
      }}
      role="status"
      aria-live="polite"
      aria-label="Finishing setup"
    >
      <div className="flex flex-col items-center w-full max-w-[480px] px-6">
        {/* Ring + logo (or fallback Statbox "S") */}
        <motion.div
          className="relative"
          style={{ width: RING_SIZE, height: RING_SIZE }}
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            scale: pulse ? [1, 1.04, 1] : 1,
            filter: pulse
              ? ['brightness(1)', 'brightness(1.15)', 'brightness(1)']
              : 'brightness(1)',
          }}
          transition={{
            opacity: { duration: RING_FADE_IN_DURATION_S, ease: EASE_OUT_EXPO },
            scale: pulse ? { duration: 0.25, ease: 'easeInOut' } : { duration: 0 },
            filter: pulse ? { duration: 0.25, ease: 'easeInOut' } : { duration: 0 },
          }}
        >
          <svg
            width={RING_SIZE}
            height={RING_SIZE}
            viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
            className="absolute inset-0"
            aria-hidden
          >
            <circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
              stroke="var(--signup-border-subtle, #e5e5e5)"
              strokeWidth={1.5}
              opacity={0.6}
            />
            <motion.circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
              stroke="var(--signup-accent)"
              strokeWidth={RING_STROKE}
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
              initial={{ strokeDashoffset: RING_CIRCUMFERENCE }}
              animate={{ strokeDashoffset: 0 }}
              transition={{ duration: RING_FILL_DURATION_S, ease: 'linear' }}
            />
          </svg>

          {/* Logo inside the ring */}
          <div className="absolute inset-0 flex items-center justify-center">
            {logoDataUrl ? (
              <div
                className="h-20 w-20 rounded-xl bg-white border border-[var(--signup-border-subtle)] flex items-center justify-center overflow-hidden"
                style={{ boxShadow: 'var(--signup-shadow-sm)' }}
              >
                <img
                  src={logoDataUrl}
                  alt=""
                  aria-hidden
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            ) : (
              <div
                className="h-20 w-20 rounded-xl flex items-center justify-center text-white"
                style={{
                  background: 'var(--signup-accent)',
                  fontWeight: 700,
                  fontSize: 36,
                  letterSpacing: '-0.02em',
                  lineHeight: 1,
                }}
                aria-hidden
              >
                S
              </div>
            )}
          </div>
        </motion.div>

        {/* Court chips — only render the row at all if we have any. */}
        {courts.length > 0 && (
          <motion.div
            className="mt-8 flex flex-wrap items-center justify-center gap-2"
            variants={containerStagger}
            initial="hidden"
            animate="show"
            aria-hidden
          >
            {visibleCourts.map((court) => (
              <motion.div
                key={court.id}
                variants={itemFadeUp}
                className="h-10 px-3 rounded-lg bg-white border border-[var(--signup-border-subtle)] flex items-center text-[13px] font-medium text-[var(--signup-text-primary)]"
              >
                {court.name || 'Court'}
              </motion.div>
            ))}
            {showOverflow && (
              <motion.div
                variants={itemFadeUp}
                className="h-10 px-3 rounded-lg bg-[var(--signup-bg-subtle)] flex items-center text-[13px] font-medium text-[var(--signup-text-secondary)]"
              >
                +{overflowCount} more
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Beat text — crossfade between values, fixed-height row to
            prevent layout shift while old/new overlap. */}
        <div className="relative mt-8 h-6 w-full">
          <AnimatePresence>
            <motion.p
              key={beatIdx}
              className="absolute inset-0 flex items-center justify-center text-[16px] font-medium text-[var(--signup-text-primary)]"
              style={{ letterSpacing: '-0.005em' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: 0.25, delay: 0.05, ease: EASE_OUT_EXPO },
              }}
            >
              {beatTexts[beatIdx]}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  )
}

/* ─────────────── Top-level mount point ─────────────── */

/**
 * Rendered once in App.jsx as a sibling to <Routes>. Subscribes to
 * the venue controller pub/sub and toggles visibility on trigger.
 *
 * Under reduced motion the loader still mounts briefly — it returns
 * null from its render and fires onComplete on the next tick so the
 * AnimatePresence cycle completes cleanly. The user lands on the
 * welcome page without a visible loader because the wizard's
 * Continue handler navigates synchronously alongside the trigger.
 */
export default function SetupVenueCompletingTransitionMount() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    return subscribeSetupVenueCompletingTransition(() => {
      setVisible(true)
    })
  }, [])

  return createPortal(
    <AnimatePresence>
      {visible && (
        <SetupVenueCompletingTransition
          key="setup-venue-completing"
          onComplete={() => setVisible(false)}
        />
      )}
    </AnimatePresence>,
    document.body,
  )
}
